import { useCallback, useEffect, useRef, useState } from 'react';

const useLocalState = (key, defaultValue) => {
  const keyRef = useRef(key);
  keyRef.current = key;

  const [value, setValue] = useState(() => {
    // When the pages are statically rendered, `window` doesn't exist
    if (typeof window !== 'undefined') {
      const storedValue = localStorage.getItem(key);
      return storedValue === null ? defaultValue : JSON.parse(storedValue);
    }
    return defaultValue;
  });

  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    const listener = (e) => {
      if (e.storageArea === localStorage && e.key === keyRef.current) {
        setValue(JSON.parse(e.newValue));
      }
    };

    window.addEventListener('storage', listener);
    return () => {
      window.removeEventListener('storage', listener);
    };
  }, []);

  const setValueInLocalStorage = useCallback((newValue) => {
    setValue((currentValue) => {
      const result =
        typeof newValue === 'function' ? newValue(currentValue) : newValue;

      // Only access localStorage in browser environment
      if (typeof window !== 'undefined') {
        // Remove item from localStorage when setting to null
        if (result === null || result === undefined) {
          localStorage.removeItem(keyRef.current);
        } else {
          localStorage.setItem(keyRef.current, JSON.stringify(result));
        }
      }

      return result;
    });
  }, []); // Remove key dependency to make function more stable

  return [value, setValueInLocalStorage];
};

export default useLocalState;
